import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { DataTable, type Rows } from './DataTable'

/**
 * A CSV is a table with one sheet. Papa Parse is loaded on demand and left to
 * detect the delimiter, since the files that reach us are as often semicolon
 * separated as comma separated.
 */
function useCsvRows(blob: Blob | null) {
  const [rows, setRows] = useState<Rows | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setRows(null)
    setError(null)

    if (!blob) return

    void (async () => {
      try {
        const [{ default: Papa }, text] = await Promise.all([import('papaparse'), blob.text()])
        if (cancelled) return

        const parsed = Papa.parse<string[]>(text, { skipEmptyLines: 'greedy' })
        setRows(parsed.data.map((row) => row.map((cell) => cell ?? '')))
      } catch {
        if (cancelled) return
        setError('Could not read this file as a table.')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [blob])

  return { rows, error }
}

export function CsvViewer({ blob, full }: { blob: Blob | null; full: boolean }) {
  const { rows, error } = useCsvRows(blob)

  if (error) {
    return <p className="px-4 py-6 text-center text-xs text-gray-400">{error}</p>
  }

  if (!rows) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    )
  }

  return <DataTable rows={rows} paginate={full} />
}
